import classNames from 'classnames'

import UndoIcon from '@mui/icons-material/Undo'
import RedoIcon from '@mui/icons-material/Redo'
import styles from './ControlPanel.module.css'

interface ControlPanelParams {
  canUndo: boolean
  canRedo: boolean
  onUndo: () => void
  onRedo: () => void
  children?: any
}

/**
 * Component for rendering a control panel with undo and redo buttons.
 *
 * @param {Object} params - The parameters for rendering the control panel.
 * @param {boolean} params.canUndo - Indicates whether the undo button should be enabled.
 * @param {boolean} params.canRedo - Indicates whether the redo button should be enabled.
 * @param {function} params.onUndo - The callback function to be called when the undo button is clicked.
 * @param {function} params.onRedo - The callback function to be called when the redo button is clicked.
 * @param {ReactNode} params.children - The child elements to be rendered within the control panel.
 * @returns {JSX.Element} The JSX element representing the `ControlPanel` component.
 */
export default function ControlPanel({ canUndo, canRedo, onUndo, onRedo, children }: ControlPanelParams) {
  return (
    <div className={styles.panel}>
      <button
        title="undo (ctrl+z)"
        className={classNames(canUndo ? undefined : styles.disabled, 'vertAligned')}
        disabled={!canUndo}
        onClick={onUndo}
      >
        <UndoIcon />
      </button>
      <button
        title="redo (ctrl+shift+z)"
        className={classNames(canRedo ? undefined : styles.disabled, 'vertAligned')}
        disabled={!canRedo}
        onClick={onRedo}
      >
        <RedoIcon />
      </button>
      {children}
    </div>
  )
}
